import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useUser } from './UserContext';
import { useDataMode } from './DataModeContext';
import { useCompany } from './CompanyContext';
import { getUserRoleService, type UserRolePermissions, type MyPermissionsResponse } from '@/services/userRoleService';
import { ADMINISTRATOR_DEFAULT_PERMISSIONS, USER_DEFAULT_PERMISSIONS } from '@/services/defaultRolePermissions';

interface PermissionsContextType {
  permissions: UserRolePermissions | null;
  isLoading: boolean;
  hasFullAccess: boolean;
  canViewReport: (reportKey: string) => boolean;
  canViewNavItem: (navKey: string) => boolean;
  canViewSettingsTab: (settingsKey: string) => boolean;
  canViewResourceDirectory: () => boolean;
  canViewResourceDetails: () => boolean;
  canManageIntegrations: () => boolean;
  canViewIntegrations: () => boolean;
  canManageCompanies: () => boolean;
  canViewCompanies: () => boolean;
  canViewRolesPermissions: () => boolean;
  canAccessSettings: () => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const { getScreenMode } = useDataMode();
  const { currentCompany } = useCompany();
  const [permissions, setPermissions] = useState<UserRolePermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFullAccess, setHasFullAccess] = useState(false);

  const mode = getScreenMode('calendar');

  const fetchPermissions = useCallback(async () => {
    setIsLoading(true);
    try {
      const service = getUserRoleService(mode);
      const response: MyPermissionsResponse = await service.getMyPermissions();
      setPermissions(response.permissions);
      setHasFullAccess(
        response.isAdminFallback ||
        response.roleName === 'Administrator'
      );
    } catch (error) {
      console.warn('[PermissionsContext] Failed to fetch permissions, using role-based fallback', error);
      // If we cannot fetch permissions, check workspace role from UserContext
      if (user.workspaceRole === 'Administrator') {
        setPermissions(ADMINISTRATOR_DEFAULT_PERMISSIONS);
        setHasFullAccess(true);
      } else {
        setPermissions(USER_DEFAULT_PERMISSIONS);
        setHasFullAccess(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [mode, user.workspaceRole, currentCompany?.id]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Refresh permissions when roles are updated (e.g., admin edits a role)
  useEffect(() => {
    const handleRolesUpdated = () => { fetchPermissions(); };
    window.addEventListener('rolesUpdated', handleRolesUpdated);
    return () => { window.removeEventListener('rolesUpdated', handleRolesUpdated); };
  }, [fetchPermissions]);

  const canViewReport = useCallback((reportKey: string): boolean => {
    if (hasFullAccess) return true;
    if (!permissions?.reportAccessPermissions) return false;
    const report = permissions.reportAccessPermissions[reportKey as keyof typeof permissions.reportAccessPermissions];
    return report?.enabled ?? false;
  }, [permissions, hasFullAccess]);

  const canViewNavItem = useCallback((navKey: string): boolean => {
    if (hasFullAccess) return true;
    if (!permissions?.navigationPermissions) return false;
    return (permissions.navigationPermissions as Record<string, boolean>)[navKey] ?? false;
  }, [permissions, hasFullAccess]);

  const canViewSettingsTab = useCallback((settingsKey: string): boolean => {
    if (hasFullAccess) return true;
    if (!permissions?.settingsPermissions) return false;
    return (permissions.settingsPermissions as Record<string, boolean>)[settingsKey] ?? false;
  }, [permissions, hasFullAccess]);

  const canViewResourceDirectory = useCallback((): boolean => {
    if (hasFullAccess) return true;
    return permissions?.resourceDirectoryPermissions?.viewDirectory ?? false;
  }, [permissions, hasFullAccess]);

  const canViewResourceDetails = useCallback((): boolean => {
    if (hasFullAccess) return true;
    return permissions?.resourceDirectoryPermissions?.viewDetails ?? false;
  }, [permissions, hasFullAccess]);

  const canManageIntegrations = useCallback((): boolean => {
    if (hasFullAccess) return true;
    return permissions?.integrationsPermissions?.manageIntegrations ?? false;
  }, [permissions, hasFullAccess]);

  const canViewIntegrations = useCallback((): boolean => {
    if (hasFullAccess) return true;
    return permissions?.integrationsPermissions?.viewIntegrations ?? false;
  }, [permissions, hasFullAccess]);

  const canManageCompanies = useCallback((): boolean => {
    if (hasFullAccess) return true;
    return permissions?.companyManagementPermissions?.manageCompanies ?? false;
  }, [permissions, hasFullAccess]);

  const canViewCompanies = useCallback((): boolean => {
    if (hasFullAccess) return true;
    return permissions?.companyManagementPermissions?.viewCompanies ?? false;
  }, [permissions, hasFullAccess]);

  const canViewRolesPermissions = useCallback((): boolean => {
    if (hasFullAccess) return true;
    return permissions?.settingsPermissions?.viewRolesPermissions ?? false;
  }, [permissions, hasFullAccess]);

  const canAccessSettings = useCallback((): boolean => {
    if (hasFullAccess) return true;
    if (!permissions?.settingsPermissions) return false;
    const sp = permissions.settingsPermissions;
    return sp.viewGeneral || sp.viewTimezone || sp.viewBookingTypeSetup || sp.viewGroupSetup || sp.viewRolesPermissions || sp.viewUserSetup;
  }, [permissions, hasFullAccess]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        isLoading,
        hasFullAccess,
        canViewReport,
        canViewNavItem,
        canViewSettingsTab,
        canViewResourceDirectory,
        canViewResourceDetails,
        canManageIntegrations,
        canViewIntegrations,
        canManageCompanies,
        canViewCompanies,
        canViewRolesPermissions,
        canAccessSettings,
        refreshPermissions: fetchPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
